/**
 * HubSpot frontend service - calls Vercel API endpoint to interact with HubSpot securely
 * Follows the same pattern as emailService.ts for consistency
 */

export interface HubSpotContactData {
  email: string;
  firstName: string;
  lastName: string;
  company?: string;
  message?: string;
  source?: string;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  journey_persona?: string;
  club___program?: string;
  telemetry_system?: string;
}

export interface HubSpotAccessRequestData {
  email: string;
  firstName: string;
  lastName: string;
  company?: string;
  organization?: string;
  requesterRole?: string;
  justification?: string;
  requestedDomain?: string;
  message?: string;
  source?: string;
}

export interface HubSpotResponse {
  success: boolean;
  contactId?: string;
  message?: string;
  error?: string;
}

/**
 * Create a HubSpot contact by calling our API endpoint
 */
export const createContact = async (contactData: HubSpotContactData): Promise<HubSpotResponse> => {
  try {
    const response = await fetch('/api/hubspot/create-contact', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(contactData),
    });

    if (!response.ok) {
      // Try to parse JSON error, but fall back to text if it fails (following emailService pattern)
      let errorMessage = 'Failed to create HubSpot contact';
      let errorDetails = '';

      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        try {
          const error = await response.json();
          errorMessage = error.error || error.message || errorMessage;
          errorDetails = error.details || '';
        } catch (e) {
          errorDetails = 'Failed to parse JSON error response';
        }
      } else {
        // Response is not JSON (likely HTML error page)
        try {
          const text = await response.text();
          errorDetails = `Server returned non-JSON response (${response.status})`;
          // Log first 100 chars of response for debugging
          if (process.env.NODE_ENV === 'development') {
            // eslint-disable-next-line no-console
            console.error('HubSpot API error response preview:', text.substring(0, 100) + '...');
          }
        } catch (textError) {
          errorDetails = 'Could not read error response';
        }
      }

      const fullError = errorDetails ? `${errorMessage}: ${errorDetails}` : errorMessage;
      throw new Error(fullError);
    }

    const result = await response.json();
    return {
      success: true,
      contactId: result.contactId,
      message: result.message,
    };
  } catch (error) {
    console.error('HubSpot service error:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to create HubSpot contact',
    };
  }
};

/**
 * Track a contact form submission in HubSpot
 * Converts form data to HubSpot format and creates contact
 */
export const trackContactForm = async (formData: {
  name: string;
  email: string;
  subject: string;
  message: string;
  source?: string;
}): Promise<HubSpotResponse> => {
  // Parse name into firstName and lastName
  const nameParts = formData.name.trim().split(' ');

  let firstName: string;
  let lastName: string;

  if (nameParts.length === 1) {
    // Single name - use same for both first and last
    firstName = nameParts[0];
    lastName = nameParts[0];
  } else if (nameParts.length === 2) {
    // Two parts - straightforward first and last
    firstName = nameParts[0];
    lastName = nameParts[1];
  } else {
    // Multiple parts - first two words are firstName, rest is lastName
    firstName = nameParts.slice(0, 2).join(' ');
    lastName = nameParts.slice(2).join(' ');
  }

  const contactData: HubSpotContactData = {
    email: formData.email,
    firstName,
    lastName,
    source: formData.source || 'Website Contact Form',
  };

  return createContact(contactData);
};

/**
 * Track an access request submission in HubSpot
 * Converts access request data to HubSpot format and creates contact
 */
export const trackAccessRequest = async (requestData: {
  email: string;
  organization: string;
  requesterRole: string;
  justification: string;
  requestedDomain: string;
}): Promise<HubSpotResponse> => {
  // Extract name from email (use email prefix as name if no name provided)
  const emailPrefix = requestData.email.split('@')[0];
  const nameParts = emailPrefix.split('.');

  let firstName: string;
  let lastName: string;

  if (nameParts.length === 1) {
    firstName = nameParts[0];
    lastName = nameParts[0];
  } else {
    firstName = nameParts[0];
    lastName = nameParts.slice(1).join(' ');
  }

  const contactData: HubSpotAccessRequestData = {
    email: requestData.email,
    firstName,
    lastName,
    company: requestData.organization,
    organization: requestData.organization,
    requesterRole: requestData.requesterRole,
    justification: requestData.justification,
    requestedDomain: requestData.requestedDomain,
    message: requestData.justification, // Pass justification as message
    source: 'Program Access Request',
  };

  return createContact(contactData);
};
